import { useMemo } from "react";
import { useSearchParams } from "react-router";
import { toast } from "react-toastify";
import { type DatasetImage } from "../api";
import t from "../translations";
import { useDatasetDetail } from "./DatasetDetailContext";

// Detection-outcome filter value: "tp" = images with at least one true
// positive, "fp" = images whose detections are only false positives.
export type DatasetImageStatus = "tp" | "fp";

// The set of filters applied to the dataset image list. Every property is
// optional: a property left undefined means that dimension is not filtered,
// and an image is shown only when it satisfies every *defined* property.
//
// To add a new filter dimension:
//   1. add an optional property here,
//   2. add its query-string key to FILTER_PARAMS,
//   3. add a line each to parseFilters, writeFilters and makeImageMatcher.
export interface DatasetImageFilters {
  // Train/test split: true = train only, false = test only, undefined = both.
  train?: boolean;
  // Detection outcome; undefined = unfiltered.
  status?: DatasetImageStatus;
  // Object type ids; an image passes when it carries at least one of them.
  objectTypes?: number[];
  // Project ids; an image passes when it belongs to one of them.
  projectIds?: number[];
}

// Query-string key backing each filter dimension.
const FILTER_PARAMS: Record<keyof DatasetImageFilters, string> = {
  train: "fSplit",
  status: "fStatus",
  objectTypes: "fTypes",
  projectIds: "fProjects",
};

// The `train` filter <-> its boolean query-string value.
function parseTrain(value: string | null): boolean | undefined {
  return value === null ? undefined : value === "true";
}

function parseStatus(value: string | null): DatasetImageStatus | undefined {
  return value === "tp" || value === "fp" ? value : undefined;
}

// A comma-separated list of ids <-> number[], shared by the objectTypes and
// projectIds filters. An empty or malformed value clears the dimension.
function parseIdList(value: string | null): number[] | undefined {
  if (!value) return undefined;
  const ids = value
    .split(",")
    .map((part) => Number(part))
    .filter((id) => Number.isInteger(id));
  return ids.length ? ids : undefined;
}

function parseFilters(params: URLSearchParams): DatasetImageFilters {
  return {
    train: parseTrain(params.get(FILTER_PARAMS.train)),
    status: parseStatus(params.get(FILTER_PARAMS.status)),
    objectTypes: parseIdList(params.get(FILTER_PARAMS.objectTypes)),
    projectIds: parseIdList(params.get(FILTER_PARAMS.projectIds)),
  };
}

function writeFilters(
  params: URLSearchParams,
  filters: DatasetImageFilters,
): void {
  if (filters.train === undefined) {
    params.delete(FILTER_PARAMS.train);
  } else {
    params.set(FILTER_PARAMS.train, String(filters.train));
  }

  if (filters.status === undefined) {
    params.delete(FILTER_PARAMS.status);
  } else {
    params.set(FILTER_PARAMS.status, filters.status);
  }

  if (!filters.objectTypes || filters.objectTypes.length === 0) {
    params.delete(FILTER_PARAMS.objectTypes);
  } else {
    params.set(FILTER_PARAMS.objectTypes, filters.objectTypes.join(","));
  }

  if (!filters.projectIds || filters.projectIds.length === 0) {
    params.delete(FILTER_PARAMS.projectIds);
  } else {
    params.set(FILTER_PARAMS.projectIds, filters.projectIds.join(","));
  }
}

// Builds a predicate that tests an image against `filters` — an image passes
// only if it satisfies every dimension that is actually set. The object-type
// ids are lifted into a Set once here rather than scanned per image.
function makeImageMatcher(
  filters: DatasetImageFilters,
  hasPositive: (image: DatasetImage) => boolean,
): (image: DatasetImage) => boolean {
  const objectTypeIds =
    filters.objectTypes && filters.objectTypes.length > 0
      ? new Set(filters.objectTypes)
      : null;
  const projectIds =
    filters.projectIds && filters.projectIds.length > 0
      ? new Set(filters.projectIds)
      : null;

  return (image) => {
    if (filters.train !== undefined && image.train !== filters.train)
      return false;
    if (filters.status === "tp" && !hasPositive(image)) return false;
    if (filters.status === "fp" && hasPositive(image)) return false;
    if (
      objectTypeIds &&
      !image.type_x_workflow_status.some(([typeId]) =>
        objectTypeIds.has(typeId),
      )
    )
      return false;
    if (projectIds && !projectIds.has(image.project_id)) return false;
    return true;
  };
}

function isFilterActive(filters: DatasetImageFilters): boolean {
  return Object.values(filters).some((value) => value !== undefined);
}

// Owns the dataset image list's filtering: it reads the images loaded by the
// dataset-detail context and narrows them to the active filter set, which is
// kept in the URL query string so the filtered view is shareable.
export function useDatasetImageFilter() {
  const { images, selectedImage, hasPositive } = useDatasetDetail();

  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);
  const filterActive = isFilterActive(filters);

  // Everything downstream — grouping, counts, keyboard navigation — works off
  // this, so a hidden image is never grouped, counted, or selected.
  const filteredImages = useMemo(
    () =>
      filterActive
        ? images.filter(makeImageMatcher(filters, hasPositive))
        : images,
    [images, filters, filterActive, hasPositive],
  );

  // Merges a partial change into the active filters and persists the result to
  // the URL. Pass a property as `undefined` to clear that dimension.
  const updateFilters = (changes: Partial<DatasetImageFilters>) => {
    const next: DatasetImageFilters = { ...filters, ...changes };
    const updated = new URLSearchParams(searchParams);
    writeFilters(updated, next);
    if (updated.toString() === searchParams.toString()) return;
    setSearchParams(updated, { replace: true });
    // Warn when the new filter set hides the image currently being viewed —
    // its row drops out of the list but the panel keeps showing it.
    if (selectedImage && !makeImageMatcher(next, hasPositive)(selectedImage)) {
      toast.info(t.ActiveImageHiddenByFilter);
    }
  };

  return {
    filters,
    filteredImages,
    filterActive,
    updateFilters,
    // The current query string, carried onto in-view navigation so filters
    // survive switching between images.
    search: searchParams.toString(),
  };
}
